import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { LayoutDashboard, Upload, AlertTriangle, Users, Menu, Search, Shield, Heart, Swords, Trophy, Star, Trash2, CalendarDays, GitCompare, FileBarChart, Flag, LogIn, LogOut, Clock } from "lucide-react";
import { CLASS_COLORS, ROLE_ORDER, getPlayerRole } from "@/lib/constants";
import { useTeams } from "@/lib/useTeams";
import { deletePlayerApi } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import BulkClassAssignment from "@/components/BulkClassAssignment";
import GuildSwitcher from "@/components/GuildSwitcher";
import { toast } from "sonner";

const ROLE_ICONS = { Tank: Shield, Healer: Heart, DPS: Swords, Unknown: Users };
const ROLE_COLORS = { Tank: "#C79C6E", Healer: "#1eff00", DPS: "#ff4444", Unknown: "#64748b" };

function isValidPlayerName(name) {
  if (!name || name.length < 2 || name.length > 30) return false;
  if (/^\d+$/.test(name)) return false;
  if (/[\[\]{}()=<>]/.test(name)) return false;
  if (/^\d{4}-\d{2}/.test(name)) return false; // timestamps
  if (/^[\d.]+$/.test(name)) return false; // just numbers/decimals
  if (/^-+$/.test(name)) return false; // just dashes
  const bad = ["general","tank","healer","dps","total","average","metric","boss",
    "trash","consumables","engineering","trinkets","stats","damage","interrupted","melee","ranged","unknown"];
  return !bad.includes(name.toLowerCase());
}

function PlayerList({ players, onNavigate, currentPath, onDataChanged }) {
  const [search, setSearch] = useState("");
  const { canDelete } = useAuth();
  const filtered = players
    .filter((p) => isValidPlayerName(p.name))
    .filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  // Group by role
  const grouped = {};
  for (const role of ROLE_ORDER) grouped[role] = [];
  for (const p of filtered) {
    const role = getPlayerRole(p.wow_class);
    if (!grouped[role]) grouped[role] = [];
    grouped[role].push(p);
  }

  const handleDeletePlayer = async (e, player) => {
    e.stopPropagation();
    if (!window.confirm(`Remove ${player.name} from roster? This deletes all their raid data.`)) return;
    try {
      await deletePlayerApi(player.id);
      toast.success(`Removed ${player.name}`);
      onDataChanged();
    } catch (err) {
      toast.error("Failed to remove player");
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="px-3 py-2">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <input
            data-testid="player-search-input"
            type="text"
            placeholder="Search players..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-secondary/50 border border-border rounded-sm pl-7 pr-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-ring"
          />
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-0.5 px-1">
          {filtered.length === 0 && (
            <p className="text-xs text-muted-foreground px-3 py-4">No players found</p>
          )}
          {ROLE_ORDER.map((role) => {
            const rolePlayers = grouped[role] || [];
            if (rolePlayers.length === 0) return null;
            const RoleIcon = ROLE_ICONS[role] || Users;
            return (
              <div key={role}>
                <div className="flex items-center gap-1.5 px-3 pt-2 pb-1" data-testid={`role-group-${role.toLowerCase()}`}>
                  <RoleIcon className="h-3 w-3" style={{ color: ROLE_COLORS[role] }} />
                  <span className="text-[10px] font-mono uppercase tracking-widest" style={{ color: ROLE_COLORS[role] }}>
                    {role} ({rolePlayers.length})
                  </span>
                </div>
                {rolePlayers.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`player-item-${p.name}`}
                    className={`player-item group ${currentPath === `/player/${p.id}` ? "active" : ""}`}
                    onClick={() => onNavigate(`/player/${p.id}`)}
                  >
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0"
                      style={{ backgroundColor: CLASS_COLORS[p.wow_class] || CLASS_COLORS.Unknown }}
                    />
                    <span
                      className="text-sm font-medium truncate flex-1"
                      style={{ color: CLASS_COLORS[p.wow_class] || CLASS_COLORS.Unknown }}
                    >
                      {p.name}
                    </span>
                    {canDelete && (
                      <button
                        data-testid={`delete-player-${p.name}`}
                        className="hidden group-hover:flex h-5 w-5 items-center justify-center rounded-sm hover:bg-destructive/20 flex-shrink-0"
                        onClick={(e) => handleDeletePlayer(e, p)}
                        title={`Remove ${p.name}`}
                      >
                        <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
}

function SidebarNav({ onNavigate, currentPath }) {
  const navItems = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/import", icon: Upload, label: "Import Data" },
    { path: "/negative-overview", icon: AlertTriangle, label: "Negative Overview" },
    { path: "/team-battle", icon: Trophy, label: "Team Battle" },
    { path: "/raid-comparison", icon: GitCompare, label: "Raid vs Raid" },
    { path: "/mvp", icon: Star, label: "MVP of the Week" },
    { path: "/weekly-report", icon: FileBarChart, label: "Weekly Report" },
    { path: "/upgrade-flags", icon: Flag, label: "Upgrade Flags" },
    { path: "/boss-history", icon: Clock, label: "Boss History" },
    { path: "/calendar", icon: CalendarDays, label: "Raid Calendar" },
  ];

  return (
    <div className="flex flex-col gap-0.5 px-2">
      {navItems.map((item) => (
        <Button
          key={item.path}
          data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
          variant="ghost"
          className={`w-full justify-start gap-2 text-sm rounded-sm h-8 ${
            currentPath === item.path
              ? "bg-secondary text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => onNavigate(item.path)}
        >
          <item.icon className="h-4 w-4" />
          {item.label}
        </Button>
      ))}
    </div>
  );
}

function SidebarContent({ team, setTeam, players, onNavigate, currentPath, onDataChanged }) {
  const teams = useTeams();
  const { user, logout, canDelete } = useAuth();
  return (
    <>
      <div className="p-4 pb-2">
        <h1 className="heading text-xl font-bold tracking-tight uppercase text-foreground">
          Raid Tracker
        </h1>
        <p className="text-xs text-muted-foreground mt-0.5 font-mono uppercase tracking-wider">
          TBC Classic
        </p>
      </div>

      {/* Auth status */}
      <div className="px-3 pb-2">
        {user ? (
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-sm bg-card/30 border border-border/20">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate" data-testid="auth-user-name">{user.name}</p>
              <p className="text-[10px] font-mono text-muted-foreground uppercase">{user.role}</p>
            </div>
            <button onClick={logout} data-testid="logout-btn"
              className="flex items-center justify-center h-6 w-6 rounded-sm text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors">
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <button onClick={() => onNavigate("/login")} data-testid="login-nav-btn"
            className="flex items-center gap-2 w-full px-2 py-1.5 rounded-sm text-xs text-muted-foreground hover:text-foreground hover:bg-card/30 transition-colors">
            <LogIn className="h-3.5 w-3.5" /> Sign In
          </button>
        )}
      </div>

      <div className="px-3 pb-2">
        {user && <GuildSwitcher />}
      </div>

      <div className="px-3 pb-2">
        <Select value={team} onValueChange={setTeam} data-testid="team-selector">
          <SelectTrigger data-testid="team-selector-trigger" className="h-8 text-sm bg-secondary/50">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {teams.map((t) => (
              <SelectItem key={t} value={t} data-testid={`team-option-${t}`}>
                {t} Team
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <SidebarNav onNavigate={onNavigate} currentPath={currentPath} />

      <Separator className="my-2" />

      <div className="px-3 pb-1 flex items-center gap-1">
        <Users className="h-3 w-3 text-muted-foreground" />
        <span className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
          Roster ({players.length})
        </span>
      </div>

      <div className="px-1 mb-1">
        <BulkClassAssignment players={players} onUpdated={onDataChanged} />
      </div>

      <PlayerList players={players} onNavigate={onNavigate} currentPath={currentPath} onDataChanged={onDataChanged} />
    </>
  );
}

export default function Sidebar({ team, setTeam, players, onNavigate, currentPath, onDataChanged }) {
  return (
    <>
      {/* Desktop sidebar */}
      <aside className="sidebar hidden lg:flex" data-testid="desktop-sidebar">
        <SidebarContent
          team={team}
          setTeam={setTeam}
          players={players}
          onNavigate={onNavigate}
          currentPath={currentPath}
          onDataChanged={onDataChanged}
        />
      </aside>

      {/* Mobile sheet */}
      <div className="lg:hidden fixed top-3 left-3 z-50">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" className="bg-card/80 backdrop-blur">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0 bg-background">
            <SheetTitle className="sr-only">Navigation</SheetTitle>
            <SidebarContent
              team={team}
              setTeam={setTeam}
              players={players}
              onNavigate={onNavigate}
              currentPath={currentPath}
              onDataChanged={onDataChanged}
            />
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
